import React from "react";
import { format } from "date-fns";
import { th, enUS } from "date-fns/locale";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/components/ui/sheet";
import {
  Copy, Check, ArrowDownToLine, ArrowUpFromLine,
  Clock, CheckCircle2, XCircle, Loader2, Hash,
  Zap, Activity, Link2, CreditCard, AlertCircle
} from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

/* ── Design Tokens ── */
const C = {
  bg: "#060C18", surface: "#0A1628", card: "#0E1C35", cardHigh: "#122040",
  border: "rgba(255,255,255,0.055)", borderMid: "rgba(255,255,255,0.09)",
  cyan: "#22D3EE", cyanSoft: "rgba(34,211,238,0.08)", cyanGlow: "rgba(34,211,238,0.18)",
  blue: "#3B82F6", blueSoft: "rgba(59,130,246,0.10)",
  emerald: "#10B981", emeraldSoft: "rgba(16,185,129,0.10)",
  amber: "#FBBF24", amberSoft: "rgba(251,191,36,0.08)",
  rose: "#FB7185", roseSoft: "rgba(251,113,133,0.10)",
  t1: "#EFF6FF", t2: "#93B4D4", t3: "#4D6E90",
};

const TDP_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');

  .tdp-sheet [data-radix-scroll-area-viewport],
  .tdp-sheet { background: #060C18 !important; border-left: 1px solid rgba(255,255,255,0.07) !important; }
  .tdp-sheet * { font-family: 'Outfit', system-ui, sans-serif; }

  .tdp-header {
    padding: 1.25rem 1.25rem 0.875rem;
    border-bottom: 1px solid rgba(255,255,255,0.055);
    background: rgba(34,211,238,0.02);
  }

  .tdp-body { background: #060C18; padding: 0 1.25rem 2rem; }

  .tdp-type-icon {
    width: 2.75rem; height: 2.75rem; border-radius: 0.875rem;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }

  .tdp-title { font-size: 1rem; font-weight: 700; color: #EFF6FF; margin: 0; }
  .tdp-subtitle { font-family: ui-monospace, monospace; font-size: 0.72rem; color: #4D6E90; margin-top: 0.25rem; }

  /* Status banner */
  .tdp-status-banner {
    border-radius: 0.875rem; padding: 1rem 1.25rem;
    margin: 1.25rem 0;
    display: flex; align-items: center; justify-content: space-between;
  }
  .tdp-status-left { display: flex; align-items: center; gap: 0.5rem; }
  .tdp-status-label { font-size: 0.9rem; font-weight: 700; }
  .tdp-amount { font-size: 1.75rem; font-weight: 800; letter-spacing: -0.03em; }

  /* Section title */
  .tdp-section-title {
    font-size: 0.68rem; font-weight: 800; color: #4D6E90;
    text-transform: uppercase; letter-spacing: 0.1em;
    margin: 1.25rem 0 0.5rem;
    display: flex; align-items: center; gap: 0.5rem;
  }
  .tdp-section-title::after { content: ''; flex: 1; height: 1px; background: rgba(255,255,255,0.055); }

  /* Info card */
  .tdp-info-card {
    background: #0E1C35;
    border: 1px solid rgba(255,255,255,0.055);
    border-radius: 0.75rem; overflow: hidden;
    margin-bottom: 0.75rem;
    transition: border-color 0.2s;
  }
  .tdp-info-card:hover { border-color: rgba(34,211,238,0.2); }

  /* Info row */
  .tdp-info-row {
    display: flex; align-items: flex-start; justify-content: space-between;
    padding: 0.6rem 0.875rem;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    transition: background 0.15s;
  }
  .tdp-info-row:last-child { border-bottom: none; }
  .tdp-info-row:hover { background: rgba(34,211,238,0.03); }
  .tdp-row-label { font-size: 0.74rem; color: #4D6E90; font-weight: 500; padding-right: 1rem; flex-shrink: 0; white-space: nowrap; }
  .tdp-row-value { font-size: 0.78rem; color: #93B4D4; text-align: right; word-break: break-all; }
  .tdp-row-value.mono { font-family: ui-monospace, monospace; color: #22D3EE; }

  /* Status indicator */
  .tdp-status-dot { width: 8px; height: 8px; border-radius: 50%; }

  /* Tiny Copy btn */
  .tdp-copy-btn {
    background: none; border: none; cursor: pointer;
    padding: 0.125rem; border-radius: 0.25rem;
    display: flex; align-items: center;
    transition: background 0.15s; flex-shrink: 0; margin-left: 4px;
  }
  .tdp-copy-btn:hover { background: rgba(34,211,238,0.12); }
  .tdp-copy-btn svg { width: 0.7rem; height: 0.7rem; color: #4D6E90; transition: color 0.15s; }
  .tdp-copy-btn:hover svg { color: #22D3EE; }

  @keyframes tdp-spin { to { transform: rotate(360deg); } }
  .tdp-spin { animation: tdp-spin 1s linear infinite; }
`;

const statusConfig = {
  success: { bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.25)", text: "#34D399", amountColor: "#34D399", icon: CheckCircle2 },
  pending: { bg: "rgba(251,191,36,0.10)", border: "rgba(251,191,36,0.25)", text: "#FBBF24", amountColor: "#22D3EE", icon: Clock },
  processing: { bg: "rgba(59,130,246,0.10)", border: "rgba(59,130,246,0.25)", text: "#60A5FA", amountColor: "#22D3EE", icon: Loader2 },
  failed: { bg: "rgba(251,113,133,0.10)", border: "rgba(251,113,133,0.25)", text: "#FB7185", amountColor: "#FB7185", icon: XCircle },
  expired: { bg: "rgba(71,85,105,0.15)", border: "rgba(71,85,105,0.28)", text: "#64748B", amountColor: "#64748B", icon: Clock },
  cancelled: { bg: "rgba(71,85,105,0.15)", border: "rgba(71,85,105,0.28)", text: "#64748B", amountColor: "#64748B", icon: XCircle },
};

export default function TransactionDetailPanel({ transaction, open, onClose }) {
  const { t, language } = useLanguage();
  const [copiedId, setCopiedId] = React.useState(null);

  if (!transaction) return null;

  const statusKey = (transaction.status || "").toLowerCase();
  const cfg = statusConfig[statusKey] || statusConfig.pending;
  const StatusIcon = cfg.icon;
  const isWithdrawal = transaction.type === "WITHDRAWAL" || (transaction.type || "").toLowerCase() === "withdrawal";

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const fmtTime = (time) => {
    if (!time) return "N/A";
    try {
      return format(new Date(time), language === "th" ? "d MMM yyyy HH:mm:ss" : "MMM d, yyyy HH:mm:ss", { locale: language === "th" ? th : enUS });
    } catch { return "N/A"; }
  };

  const InfoRow = ({ label, value, mono = false, copyable = false }) => (
    <div className="tdp-info-row">
      <span className="tdp-row-label">{label}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span className={`tdp-row-value ${mono ? "mono" : ""}`}>{value || "N/A"}</span>
        {copyable && value && (
          <button className="tdp-copy-btn" onClick={() => copy(value)}>
            {copiedId === value ? <Check style={{ color: C.emerald }} /> : <Copy />}
          </button>
        )}
      </div>
    </div>
  );

  const SectionTitle = ({ label }) => (
    <p className="tdp-section-title">{label}</p>
  );

  return (
    <>
      <style>{TDP_CSS}</style>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent className="tdp-sheet w-full sm:max-w-lg overflow-y-auto" style={{ border: "none" }}>
          
          <SheetHeader className="tdp-header">
            <SheetTitle style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <div className="tdp-type-icon" style={{
                background: isWithdrawal ? "rgba(251,191,36,0.10)" : "rgba(59,130,246,0.10)",
                border: `1px solid ${isWithdrawal ? "rgba(251,191,36,0.25)" : "rgba(59,130,246,0.25)"}`,
              }}>
                {isWithdrawal
                  ? <ArrowUpFromLine style={{ width: "1.25rem", height: "1.25rem", color: C.amber }} />
                  : <ArrowDownToLine style={{ width: "1.25rem", height: "1.25rem", color: C.blue }} />}
              </div>
              <div style={{ flex: 1 }}>
                <p className="tdp-title">
                  {isWithdrawal ? (t("withdrawalDetails") || "Withdrawal Details") : (t("depositDetails") || "Deposit Details")}
                </p>
                <p className="tdp-subtitle">{transaction.transaction_id || transaction.payment_id || "N/A"}</p>
              </div>
            </SheetTitle>
          </SheetHeader>

          <div className="tdp-body" style={{ paddingTop: "1rem" }}>
            
            {/* Status Banner */}
            <div className="tdp-status-banner" style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}>
              <div className="tdp-status-left">
                <StatusIcon className={statusKey === "processing" ? "tdp-spin" : ""} style={{ width: "1.1rem", height: "1.1rem", color: cfg.text }} />
                <span className="tdp-status-label" style={{ color: cfg.text }}>{t(statusKey) || transaction.status}</span>
              </div>
              <span className="tdp-amount" style={{ color: cfg.amountColor }}>
                {isWithdrawal ? "−" : "+"}฿{(transaction.amount || 0).toLocaleString()}
              </span>
            </div>

            {transaction.error_message && (
              <div style={{ background: C.roseSoft, border: `1px solid ${C.roseSoft}`, borderRadius: "0.75rem", padding: "0.75rem 1rem", marginBottom: "1.25rem" }}>
                <p style={{ color: C.rose, fontSize: "0.8rem", margin: 0 }}>{transaction.error_message}</p>
              </div>
            )}

            {/* Transaction Info */}
            <SectionTitle label={t("transactionInfo") || "TRANSACTION INFO"} />
            <div className="tdp-info-card">
              <InfoRow label={t("id") || "ID"} value={transaction.transaction_id || transaction.payment_id} mono copyable />
              <InfoRow label={t("reference") || "Reference"} value={transaction.merchant_ref || transaction.reference} mono copyable />
              <InfoRow label={t("currency") || "Currency"} value={transaction.currency || "THB"} />
              <InfoRow label={t("grossAmount") || "Gross Amount"} value={`฿${(transaction.amount || 0).toLocaleString()}`} />
              <InfoRow label={t("totalFee") || "Total Fee"} value={`฿${(transaction.fee_amount || transaction.total_fee || 0).toLocaleString()}`} />
              <InfoRow label={t("netAmount") || "Net Amount"} value={`฿${(transaction.net_amount || (transaction.amount - (transaction.fee_amount || 0)) || 0).toLocaleString()}`} />
            </div>

            {/* Processing Info */}
            <SectionTitle label={t("processingInfo") || "PROCESSING INFO"} />
            <div className="tdp-info-card">
              <InfoRow label={t("provider") || "Provider"} value={transaction.provider_code || transaction.client_bank_name} />
              <InfoRow label={t("paymentMethod") || "Payment Method"} value={transaction.payment_method?.replace(/_/g, " ")} />
              {transaction.client_bank_account && (
                <InfoRow label={t("bankAccount") || "Bank Account"} value={transaction.client_bank_account} mono copyable />
              )}
            </div>

            {/* Callback Info */}
            {(transaction.callback_url || transaction.callback_status) && (
              <>
                <SectionTitle label={t("callbackInfo") || "CALLBACK INFO"} />
                <div className="tdp-info-card">
                  <InfoRow label={t("status") || "Status"} value={t(transaction.callback_status?.toLowerCase()) || transaction.callback_status} />
                  <InfoRow label={t("attempts") || "Attempts"} value={transaction.callback_attempts || 0} />
                  <InfoRow label={t("url") || "URL"} value={transaction.callback_url} mono />
                </div>
              </>
            )}

            {/* Timestamps */}
            <SectionTitle label={t("timestamps") || "TIMESTAMPS"} />
            <div className="tdp-info-card">
              <InfoRow label={t("created") || "Created"} value={fmtTime(transaction.created_date || transaction.createdAt)} />
              <InfoRow label={t("updated") || "Updated"} value={fmtTime(transaction.updated_at || transaction.updatedAt || transaction.completed_at)} />
            </div>

            {/* Webhook Data */}
            {transaction.webhook_data && (
              <>
                <SectionTitle label={t("webhooks") || "WEBHOOKS"} />
                <div className="tdp-info-card" style={{ padding: "0.875rem" }}>
                   <p style={{ fontSize: "0.7rem", color: C.t3, marginBottom: "0.5rem" }}>{t("rawWebhookData") || "Raw Webhook Data"}</p>
                   <pre style={{
                     background: C.bg, color: C.t2, padding: "0.75rem",
                     borderRadius: "0.5rem", fontSize: "0.72rem", overflowX: "auto",
                     maxHeight: "150px", border: `1px solid ${C.border}`, margin: 0,
                     fontFamily: "ui-monospace, monospace"
                   }}>
                     {transaction.webhook_data}
                   </pre>
                </div>
              </>
            )}

          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}